import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { compose, setDisplayName } from 'recompose';
import { isEqual } from 'lodash';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { grey700 } from '@material-ui/core/colors';
import { withStyles } from '@material-ui/core/styles';
import uuidv1 from 'uuid/v1';
import { usStates } from 'constants/options';

import {
  injectIntl,
  SmartTable,
  InvoiceBadge,
} from 'components';
import { FRInput } from 'elements';
import MenuItem from '@material-ui/core/MenuItem';

const styles = () => ({
  disabledButton: { background: 'gray !important' },
  disabledButtonAlign: { textAlign: 'right' },
  dropDownItem: { padding: '0px 10px' },
});

class AddressForm extends Component {
  static contextTypes = {
    currentUser: PropTypes.shape({}),
  };

  static propTypes = {
    addresses: PropTypes.arrayOf(
      PropTypes.shape(),
    ).isRequired,
    classes: PropTypes.shape({}).isRequired,
    formatMessage: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    isRetailPage: PropTypes.bool,
    asConsumerForm: PropTypes.bool,
  };

  static defaultProps = {
    isRetailPage: false,
    asConsumerForm: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      addresses: this.addId(props.addresses),
    };
  }

  componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.addresses, nextProps.addresses)) {
      this.setState({ addresses: this.addId(nextProps.addresses) });
    }
  }

  getFields = (mobile = false) => {
    const { classes, formatMessage, isRetailPage, asConsumerForm } = this.props;
    const { currentUser } = this.context;
    const currentAccount = currentUser.account || {}; 
    const role = {
      2: 'Buyer',
      3: 'Distributor',
      4: 'Seller',
      5: 'Consumer',
    }[currentAccount.currentRoleId] || 'Personal';
    const { addresses } = this.state;
    const stateOptions = usStates.map(d => (
      <option
        key={d.code}
        value={d.code}
      >
        {d.code}
      </option>),
    );

    const render = (row, index, field) => (field.name === 'state' ?
      <Select
        name={field.name}
        value={row[field.name]}
        inputProps={{
          required: true,
        }}
        native
        onChange={e => this.updateAddressLine(index, field.name, e.target.value)}
      >
        <option value="" />
        {stateOptions}
      </Select> :
      <FRInput
        value={row[field.name]}
        name={field.name}
        placeholder={field.label}
        style={{ width: '100%' }}
        required
        onChange={e => this.updateAddressLine(index, field.name, e.target.value)}
      />
    );

    const fields = [];
    if (isRetailPage) {
      fields.push({
        label: '',
        name: 'default',
        render: (row, index) => (
          <InvoiceBadge type={'default'}>
            {index === 0 ? 'Default' : ''}
          </InvoiceBadge>
        ),
      });
    }
    fields.push({
      label: formatMessage('Address *'),
      name: 'address',
      render,
    },
    {
      label: formatMessage('City *'),
      name: 'city',
      render,
    },
    {
      label: formatMessage('State *'),
      name: 'state',
      render,
    },
    {
      label: formatMessage('Zipcode *'),
      name: 'zipcode',
      render,
    },
    {
      name: 'action',
      type: 'controlBtns',
      render: (row, index) => (
        <div className={`${role === 'Consumer' ? classes.disabledButtonAlign : ''}`}>
          <Fragment>
            <IconButton
              classes={{
                disabled: classes.disabledButton,
              }}
              disabled={addresses.length <= 1}
              onClick={() => {
                const { addresses } = this.state;
                this.props.onUpdate();
                this.setState({
                  addresses: [...addresses.slice(0, index), ...addresses.slice(index + 1)],
                });
              }}
            >
              <DeleteIcon color={grey700} />
            </IconButton>
          </Fragment>
        </div>
      ),
    });

    return fields;
  };

  addId = addresses => (
    addresses.length
      ? addresses.map(addr => ({
        id: uuidv1(),
        ...addr,
      }))
      : [{ id: uuidv1(), address: '', city: '', state: '', zipcode: '' }]
  );

  insertAddressLine = (index) => {
    const { addresses } = this.state;

    this.props.onUpdate();

    this.setState({
      addresses: [
        ...addresses.slice(0, index),
        { id: uuidv1() },
        ...addresses.slice(index),
      ],
    });
  };

  updateAddressLine = (index, field, value) => {
    const addresses = this.state.addresses.slice();
    addresses[index][field] = value;

    this.props.onUpdate();

    this.setState({
      addresses,
    });
  };

  render() {
    const { currentUser } = this.context;
    const currentAccount = currentUser.account || {}; 
    const role = {
      2: 'Buyer',
      3: 'Distributor',
      4: 'Seller',
      5: 'Consumer',
    }[currentAccount.currentRoleId] || 'Personal';
    const { addresses } = this.state;
    const { isRetailPage, asConsumerForm } = this.props;
    const isSetting = true;
    return (
      <Fragment>
        {isRetailPage && (
          <div style={{ borderTop: '1px solid #CACACA', borderBottom: '1px solid #CACACA', paddingTop: '15px', paddingBottom: '5px' }}>
            <div className="d-flex justify-content-center align-items-center" style={{ marginBottom: '-45px' }}>
              <div style={{ textTransform: 'uppercase', fontSize: '24px' }}>{'Pick-Up Locations'}</div>
            </div>
            <div className="d-flex justify-content-end align-items-center">
              <div style={{ marginRight: '20px' }}>
                <Button
                  color="primary"
                  variant="fab"
                  onClick={() => this.insertAddressLine(addresses.length)}
                >
                  <AddIcon color={grey700} />
                </Button>
              </div>
              <div style={{ color: 'rgba(0, 0, 0, 0.54)', textTransform: 'uppercase' }}>
                Location
              </div>
            </div>
          </div>
        )}
        <SmartTable
          data={addresses}
          fields={this.getFields()}
          fieldsMobile={this.getFields(true)}
          isSetting={isSetting}
          isRetailPage={isRetailPage}
        />
        {!isRetailPage && !asConsumerForm && role !== 'Consumer' && (
          <div className="d-flex justify-content-end align-items-center">
            <div style={{ marginRight: '20px' }}>
              <Button
                color="primary"
                variant="fab"
                onClick={() => this.insertAddressLine(addresses.length)}
              >
                <AddIcon color={grey700} />
              </Button>
            </div>
            <div style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
              Another Address
            </div>
          </div>
        )}
      </Fragment>
    );
  }
}

export default compose(
  setDisplayName('AddressForm'),
  withStyles(styles),
  injectIntl,
)(AddressForm);
